import { getOverviewBlockTitle } from '../../content';
import { SWARM_FEED_ALL, type SwarmFeedOption } from '../../functions/dataFilters/swarmFeed';
import type { Risk } from '../../types/api';
import { SwarmFeedCard } from './SwarmFeedCard';

interface SwarmFeedProps {
  records: Risk[];
  loading: boolean;
  error: boolean;
  locationKey: string;
  operatingModelKey: string;
  locationOptions: SwarmFeedOption[];
  operatingModelOptions: SwarmFeedOption[];
  onLocationChange: (key: string) => void;
  onOperatingModelChange: (key: string) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
}

function recordKey(risk: Risk, index: number): string {
  return [
    risk.publication_date ?? '',
    risk.country ?? '',
    risk.location ?? '',
    risk.operating_mode ?? '',
    risk.impact_target ?? '',
    index,
  ].join('|');
}

function FilterSelect({
  id,
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: SwarmFeedOption[];
  disabled: boolean;
  onChange: (key: string) => void;
}) {
  return (
    <label className="overview-swarm-feed__filter" htmlFor={id}>
      <span className="overview-swarm-feed__filter-label">{label}</span>
      <select
        id={id}
        className="overview-swarm-feed__select"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
      >
        <option value={SWARM_FEED_ALL}>All</option>
        {options.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function SwarmFeed({
  records,
  loading,
  error,
  locationKey,
  operatingModelKey,
  locationOptions,
  operatingModelOptions,
  onLocationChange,
  onOperatingModelChange,
  hasMore,
  loadingMore,
  onLoadMore,
}: SwarmFeedProps) {
  const title = getOverviewBlockTitle('swarmFeed', 'Swarm Feed');
  const statusMessage = error
    ? 'Swarm records could not be loaded.'
    : loading
      ? 'Loading swarm feed…'
      : records.length === 0
        ? 'No swarm records match this chart, date range, and filters.'
        : '';

  return (
    <aside className="overview-swarm-feed" aria-label={title}>
      <header className="overview-swarm-feed__header">
        <h3 className="overview-swarm-feed__title">{title}</h3>
      </header>

      <div className="overview-swarm-feed__filters">
        <FilterSelect
          id="swarm-feed-location"
          label="Country & City"
          value={locationKey}
          options={locationOptions}
          disabled={loading || error}
          onChange={onLocationChange}
        />
        <FilterSelect
          id="swarm-feed-operating-model"
          label="Operating Model"
          value={operatingModelKey}
          options={operatingModelOptions}
          disabled={loading || error}
          onChange={onOperatingModelChange}
        />
      </div>

      {statusMessage ? (
        <p className="overview-swarm-feed__status">{statusMessage}</p>
      ) : (
        <ul className="overview-swarm-feed__list">
          {records.map((risk, index) => (
            <li key={recordKey(risk, index)}>
              <SwarmFeedCard risk={risk} />
            </li>
          ))}
        </ul>
      )}
      {hasMore && !error && (
        <button
          type="button"
          className="overview-swarm-feed__load-more"
          disabled={loadingMore}
          onClick={onLoadMore}
        >
          {loadingMore ? 'Loading more…' : 'Load more'}
        </button>
      )}
    </aside>
  );
}
